/**
 * Analytics Layout — module renamed from "Reports" (Jun 2026, ANALYTICS-RENAME-001)
 * A natural extension of TitleFlow:
 *  • Standard frosted 56px header (logo, primary nav pills, profile chip)
 *  • Horizontal tabs (no left sidebar) — full width for analytics
 *  • Access Level panel removed (role scoping is enforced server-side)
 */
import React from 'react';
import { useNavigate, useLocation, Outlet, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../App';
import {
  BarChart3, Clock, TrendingDown, TrendingUp, ChevronLeft,
  Target, ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { AnPageHeader } from './analytics/ui/AnalyticsUi';

// Roles that can access reports
const REPORTS_ALLOWED_ROLES = [
  'admin', 
  'hr', 
  'hr_manager', 
  'hr_specialist',
  'team_leader', 
  'tax_team_leader', 
  'title_team_leader', 
  'title_search_team_leader', 
  'tax_search_team_leader',
  'manager', 
  'supervisor',
  'processor',
  'tax_processor',
  'search_processor',
  'title_search_processor'
];

// Attendance / Downtime are management reports; processors only get Productivity
// ANALYTICS-TL-ACCESS-001 (Jun 2026): Tax/Title Search Team Leaders removed —
// their Analytics nav is Productivity | Performance Targets | QC only.
const MANAGEMENT_ROLES = [
  'admin', 'hr', 'hr_manager', 'hr_specialist', 'team_leader', 'tax_team_leader', 'title_team_leader',
  'manager', 'supervisor'
];

// Performance Targets management — Admin + Team Leaders only (no Manager persona)
const TARGET_MANAGER_ROLES = [
  'admin', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'
];

// ANALYTICS-HEADER-001 (Jun 2026): page-specific header meta per tab —
// the generic "Analytics" heading was removed for a compact header row.
const PAGE_META = {
  '/analytics/attendance': { title: 'Attendance Reports', desc: 'View and export attendance data for HR, payroll, and audit compliance', icon: Clock },
  '/analytics/downtime': { title: 'Downtime Analytics', desc: 'System downtime metrics and trends', icon: TrendingDown },
  '/analytics/productivity': { title: 'Productivity Analytics', desc: 'File completion metrics and performance trends', icon: TrendingUp },
  '/analytics/targets': { title: 'Performance Targets', desc: 'Set and track daily completion targets per user', icon: Target },
  '/analytics/qc': { title: 'QC Analytics', desc: 'Quality metrics, error trends and external errors', icon: ShieldCheck },
};

const ReportsLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const canAccessReports = REPORTS_ALLOWED_ROLES.includes(user?.role);
  const isManagement = MANAGEMENT_ROLES.includes(user?.role);
  const canManageTargets = TARGET_MANAGER_ROLES.includes(user?.role);

  const navItems = [
    { path: '/analytics/attendance', label: 'Attendance Reports', icon: Clock, visible: isManagement },
    { path: '/analytics/downtime', label: 'Downtime Analytics', icon: TrendingDown, visible: isManagement },
    { path: '/analytics/productivity', label: 'Productivity Analytics', icon: TrendingUp, visible: true },
    { path: '/analytics/targets', label: 'Performance Targets', icon: Target, visible: canManageTargets },
    // QUALITY-PHASE2: QC quality metrics + External Errors (Admin + TLs — same scope as Targets)
    { path: '/analytics/qc', label: 'QC Analytics', icon: ShieldCheck, visible: canManageTargets }
  ].filter((item) => item.visible);

  // Browser tab title while inside the Analytics module
  React.useEffect(() => {
    document.title = 'Analytics — TitleFlow';
    return () => { document.title = 'TitleFlow'; };
  }, []);

  // HR-ANALYTICS-SCOPE-001 (Aug 25, 2026): HR portal roles never use the
  // generic Analytics shell — attendance reporting lives inside the HR
  // Portal at /admin/hr/attendance-reports. Backend also 403s them on
  // downtime/productivity routes. (Placed after all hooks — rules of hooks.)
  if (user?.role === 'hr_manager' || user?.role === 'hr_specialist') {
    return <Navigate to="/admin/hr/attendance-reports" replace />;
  }

  // ANALYTICS-TL-ACCESS-001: Search TLs cannot deep-link into the removed
  // Attendance / Downtime views — redirect to Productivity Analytics.
  if ((user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader')
      && (location.pathname.startsWith('/analytics/attendance') || location.pathname.startsWith('/analytics/downtime'))) {
    return <Navigate to="/analytics/productivity" replace />;
  }

  const isActivePath = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const pageMeta = Object.entries(PAGE_META).find(([p]) => isActivePath(p))?.[1]
    || { title: 'Analytics', desc: 'Live operations, reporting, performance targets and executive insights', icon: BarChart3 };
  const PageIcon = pageMeta.icon;

  if (!canAccessReports) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
          <p className="text-gray-600 mb-4">
            Analytics is available to Team Leaders and Admins only.
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reports-layout">
      <div className="mx-auto w-full" style={{ maxWidth: '1440px' }}>
        {/* Compact header: page-specific title/description + right-aligned tabs */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 relative">
          <AnPageHeader icon={PageIcon} title={pageMeta.title} desc={pageMeta.desc} />
          <div className="flex flex-col items-end gap-1">
          <span className="an-accent an-rise hidden xl:block text-[13px] leading-none pr-1 pointer-events-none select-none" data-testid="analytics-header-accent">“People power performance.”</span>
          <nav
            className="inline-flex items-center rounded-xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04)] p-1 gap-0.5 max-w-full overflow-x-auto"
            data-testid="reports-tab-bar"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActivePath(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.split('/').pop()}`}
                  className={`an-tab flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-medium whitespace-nowrap ${
                    active ? 'is-active' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-slate-400'}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          </div>
        </div>

        <Outlet />
      </div>
    </div>
  );
};

export default ReportsLayout;
